/* =====================================================================
   LP "IA para estudar" — /ajudante-ia-dever-casa/ (lp_ia_estudar.php).
   Comportamento da classe Component do design "Lume - LP IA para Estudar":
   texto fantasma, caderno com virar de página, snap à pauta, contadores,
   reveal, barra fixa, e o campo que guarda a pergunta e passa ao login.

   O HTML do servidor já é a página completa; isto só corre por cima.
   ===================================================================== */
import { isInAppBrowser, openInBrowserHref } from './lume/lib/in-app-browser';
import { clearPendingQuestion, savePendingQuestion } from './lume/lib/pending-question';
import { AuthCookies } from './main/user-area/utils/auth-cookies';
import { addUserLoggedEventListener } from './main/user-area/utils/listeners';

const LOCATION = 'lp_ia_estudar';
const MSG_EMPTY = 'Escreva a questão ou mande uma foto.';
const MSG_PHOTO = 'Não conseguimos salvar a foto. Tente uma foto menor ou escreva a questão.';
// O mesmo tecto do composer do Lume (MAX_MESSAGE_CHARS em ChatComposer.tsx)
const MAX_QUESTION_CHARS = 4000;
const PHOTO_MAX_SIDE = 1600;
const PHOTO_QUALITY = 0.8;
const GHOST_SAMPLES = ['Resolva 2x + 7 = 19', 'Por que a Revolução Francesa começou?', 'Balanceie: H₂ + O₂ → H₂O', 'Qual a diferença entre mitose e meiose?'];

// Braço do A/B lp-ia-estudar-hero_v1, escrito no <main> pelo script do palco
function variant() {
    const main = document.querySelector('.lp-ia');
    return (main && main.getAttribute('data-lpi-variant')) || 'a';
}

// Só dimensões já registadas no GA4 (as do lume-events.ts e do funil). As três
// vão sempre, mesmo vazias: o GTM herda as chaves do push anterior.
function push(event, element, type) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event,
        location: LOCATION,
        element,
        type,
        experiment_id: 'lp-ia-estudar-hero_v1',
        experiment_variant: variant(),
    });
}

const slug = (t) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
        img.src = url;
    });
}

// Uma foto de celular em base64 passa o limite do localStorage: lado maior até
// 1600px e JPEG ~0,8 antes de guardar.
async function compressPhoto(file) {
    const img = await loadImage(file);
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', PHOTO_QUALITY);
}

(function initLpIaEstudar() {
    const root = document.querySelector('.lp-ia');
    if (!root) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasIO = 'IntersectionObserver' in window;
    const chatUrl = root.getAttribute('data-chat-url');

    const stage = root.querySelector('.lpi-stage');
    const sheet = root.querySelector('#lpi-sheet');
    const card = root.querySelector('#lpi-ask-card');
    const form = card.querySelector('.lpi-ask-form');
    const login = card.querySelector('.lpi-ask-login');
    const ta = root.querySelector('#lpi-question');
    const errorEl = root.querySelector('#lpi-error');
    const photoInput = root.querySelector('#lpi-photo');
    const photoLabel = root.querySelector('.lpi-photo-label');
    const work = root.querySelector('#lpi-work');
    const leafHost = root.querySelector('.lpi-leaf-host');
    const tabs = Array.prototype.slice.call(root.querySelectorAll('.lpi-tabs [role="tab"]'));

    let demos = [];
    try {
        demos = JSON.parse(stage.getAttribute('data-demos')) || [];
    } catch (e) {
        demos = [];
    }

    let current = 0;
    let flipping = false;
    let image = null;
    let awaitingLogin = false;

    /* ── o campo ocupa um número inteiro de pautas ─────────────────── */
    function snap() {
        const line = parseFloat(getComputedStyle(sheet).getPropertyValue('--nb-line')) || 36;
        card.style.minHeight = '';
        const h = card.getBoundingClientRect().height;
        card.style.minHeight = (Math.ceil(h / line - 0.02) * line) + 'px';
    }

    /* ── texto fantasma ────────────────────────────────────────────── */
    const ghost = root.querySelector('.lpi-ghost-text');
    let ghostTimer = 0;
    let ghostAlive = !reduced;

    function stopGhost() {
        ghostAlive = false;
        clearTimeout(ghostTimer);
    }

    function startGhost() {
        if (!ghost || !ghostAlive) return;
        // O servidor já escreveu a primeira amostra: começa por apagá-la
        let si = 0;
        let ci = GHOST_SAMPLES[0].length;
        let typing = false;
        const step = () => {
            if (!ghostAlive) return;
            const s = GHOST_SAMPLES[si];
            if (typing) {
                ci += 1;
                ghost.textContent = s.slice(0, ci);
                if (ci >= s.length) {
                    typing = false;
                    ghostTimer = setTimeout(step, 1600);
                    return;
                }
                ghostTimer = setTimeout(step, 42 + Math.random() * 50);
            } else {
                ci -= 2;
                if (ci <= 0) {
                    ci = 0;
                    typing = true;
                    si = (si + 1) % GHOST_SAMPLES.length;
                    ghost.textContent = '';
                    ghostTimer = setTimeout(step, 420);
                    return;
                }
                ghost.textContent = s.slice(0, ci);
                ghostTimer = setTimeout(step, 18);
            }
        };
        ghostTimer = setTimeout(step, 2200);
    }

    /* ── caderno: separadores e virar a página ─────────────────────── */
    const isEq = (t) => /[=→]/.test(t);

    function renderDemo(i) {
        const d = demos[i];
        if (!d) return;
        const field = (name) => work.querySelector('[data-f="' + name + '"]');
        field('subject').textContent = d.subject;
        const q = field('q');
        q.textContent = d.q;
        q.classList.toggle('is-eq', !!d.eq);
        const a = field('a');
        a.textContent = d.a;
        a.classList.toggle('is-eq', isEq(d.a) && d.a.length < 28);
        a.classList.toggle('is-long', !(isEq(d.a) && d.a.length < 28) && d.a.length > 60);
        const ol = field('why');
        ol.textContent = '';
        d.why.forEach((t) => {
            const li = document.createElement('li');
            li.textContent = t;
            if (isEq(t) && t.length < 40) li.className = 'is-eq';
            ol.appendChild(li);
        });
        field('next').textContent = d.next;
        field('note').textContent = d.note;

        tabs.forEach((tab, k) => {
            tab.setAttribute('aria-selected', k === i ? 'true' : 'false');
            tab.setAttribute('tabindex', k === i ? '0' : '-1');
        });
        work.setAttribute('aria-labelledby', 'lpi-tab-' + i);
        current = i;
    }

    function ink() {
        if (reduced) return;
        work.classList.remove('is-ink');
        void work.offsetWidth;
        work.classList.add('is-ink');
    }

    // Cópia estática da folha para a página que vira. Sem ids, sem data-eng-*
    // (o engagement-tracker contaria os clones) e sem o botão do Google.
    function face() {
        const inner = document.createElement('div');
        inner.className = 'lpi-leaf-inner';
        inner.style.width = sheet.offsetWidth + 'px';
        Array.prototype.forEach.call(sheet.children, (child) => {
            if (!child.classList.contains('lpi-ask') && !child.classList.contains('lpi-work')) return;
            const k = child.cloneNode(true);
            k.removeAttribute('id');
            k.querySelectorAll('[id]').forEach((e) => e.removeAttribute('id'));
            k.querySelectorAll('[data-eng-location]').forEach((e) => {
                e.removeAttribute('data-eng-location');
                e.removeAttribute('data-eng-element');
                e.removeAttribute('data-eng-type');
            });
            k.querySelectorAll('.lpi-peek, .lpi-google').forEach((e) => e.remove());
            k.querySelectorAll('textarea').forEach((t) => { t.value = ta.value; });
            k.querySelectorAll('.lpi-attach-remove').forEach((e) => e.remove());
            k.classList.remove('is-ink');
            inner.appendChild(k);
        });
        return { node: inner, h: sheet.offsetHeight };
    }

    function leaf(f, flat) {
        const el = document.createElement('div');
        el.className = 'lpi-leaf' + (flat ? ' is-flat' : '');
        el.style.height = f.h + 'px';
        const front = document.createElement('div');
        front.className = 'lpi-leaf-face';
        front.appendChild(f.node);
        const shade = document.createElement('div');
        shade.className = 'lpi-leaf-shade';
        front.appendChild(shade);
        el.appendChild(front);
        let back = null;
        if (!flat) {
            back = document.createElement('div');
            back.className = 'lpi-leaf-face lpi-leaf-back';
            el.appendChild(back);
        }
        leafHost.appendChild(el);
        return { leaf: el, shade, front, back };
    }

    function pick(i) {
        if (i === current || flipping || !demos[i]) return;
        push('lume_demo_played', slug(demos[i].subject));

        if (reduced || !sheet.animate) {
            renderDemo(i);
            snap();
            ink();
            return;
        }

        const forward = i > current;
        const old = face();
        flipping = true;
        renderDemo(i);
        snap();
        leafHost.textContent = '';

        let m;
        if (forward) {
            m = leaf(old);
        } else {
            leaf(old, true);
            m = leaf(face());
        }
        const opts = { duration: 820, easing: 'cubic-bezier(.5,.05,.25,1)', fill: 'forwards' };
        const kf = forward
            ? [{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(-12deg) translateZ(6px)', offset: 0.12 }, { transform: 'rotateY(-180deg)' }]
            : [{ transform: 'rotateY(-180deg)' }, { transform: 'rotateY(-168deg) translateZ(6px)', offset: 0.88 }, { transform: 'rotateY(0deg)' }];
        const anim = m.leaf.animate(kf, opts);
        m.shade.animate([{ opacity: 0 }, { opacity: 1, offset: 0.45 }, { opacity: 0 }], opts);
        // A troca de faces é explícita, no instante em que a folha fica de perfil
        // (-90°), e o fade vive nas faces e não na folha: opacity na .lpi-leaf
        // obriga o browser a achatar o preserve-3d, o backface-visibility deixa
        // de contar e a frente aparece espelhada na segunda metade.
        const edge = forward ? 0.12 + (78 / 168) * 0.88 : 0.88 + (78 / 168) * 0.12;
        const shown = (from, to) => [{ opacity: from }, { opacity: from, offset: edge }, { opacity: to, offset: Math.min(1, edge + 0.001) }, { opacity: to }];
        m.front.animate(forward ? shown(1, 0) : shown(0, 1), opts);
        m.back.animate(forward
            ? [{ opacity: 0 }, { opacity: 0, offset: edge }, { opacity: 1, offset: edge + 0.001 }, { opacity: 1, offset: 0.82 }, { opacity: 0 }]
            : [{ opacity: 0 }, { opacity: 1, offset: 0.18 }, { opacity: 1, offset: edge }, { opacity: 0, offset: edge + 0.001 }, { opacity: 0 }], opts);
        const done = () => {
            leafHost.textContent = '';
            flipping = false;
            ink();
        };
        anim.onfinish = done;
        anim.oncancel = done;
    }

    tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => pick(i));
        tab.addEventListener('keydown', (e) => {
            const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
            let next = null;
            if (keys[e.key]) next = (i + keys[e.key] + tabs.length) % tabs.length;
            if (e.key === 'Home') next = 0;
            if (e.key === 'End') next = tabs.length - 1;
            if (next === null) return;
            e.preventDefault();
            tabs[next].focus();
            pick(next);
        });
    });

    /* ── campo, foto e login ───────────────────────────────────────── */
    function showError(msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
        card.classList.add('is-invalid');
        snap();
    }

    function clearError() {
        if (errorEl.hidden) return;
        errorEl.hidden = true;
        errorEl.textContent = '';
        card.classList.remove('is-invalid');
        snap();
    }

    function syncHasText() {
        card.classList.toggle('has-text', ta.value.trim().length > 0 || !!image);
    }

    card.querySelector('.lpi-ask-field').addEventListener('click', () => ta.focus());
    ta.addEventListener('focus', stopGhost);
    function fitTextarea() {
        ta.style.height = 'auto';
        ta.style.height = Math.min(180, ta.scrollHeight) + 'px';
    }

    ta.addEventListener('input', () => {
        fitTextarea();
        syncHasText();
        clearError();
    });

    card.querySelector('[data-action="photo"]').addEventListener('click', () => photoInput.click());
    // Pré-visualização com remover, como o composer do Lume (ChatComposer.tsx).
    // O token descarta uma preparação que acabe depois de uma escolha mais nova
    // ou de um remover.
    const attach = card.querySelector('.lpi-attach');
    const attachImg = attach.querySelector('img');
    const attachStatus = attach.querySelector('.lpi-attach-status');
    let photoToken = 0;
    let preparing = false;

    function clearPhoto() {
        photoToken += 1;
        preparing = false;
        image = null;
        attach.hidden = true;
        attachImg.hidden = true;
        attachImg.removeAttribute('src');
        photoLabel.textContent = 'Tirar foto';
        syncHasText();
        snap();
    }

    photoInput.addEventListener('change', async () => {
        const file = photoInput.files && photoInput.files[0];
        photoInput.value = '';
        if (!file) return;
        const token = ++photoToken;
        stopGhost();
        clearError();
        image = null;
        preparing = true;
        attachImg.hidden = true;
        attachStatus.textContent = 'Preparando a foto…';
        attach.hidden = false;
        card.classList.add('has-text');
        snap();
        let prepared = null;
        try {
            prepared = await compressPhoto(file);
        } catch (e) {
            prepared = null;
        }
        if (token !== photoToken) return;
        if (!prepared) {
            clearPhoto();
            showError(MSG_PHOTO);
            return;
        }
        preparing = false;
        image = prepared;
        attachImg.src = prepared;
        attachImg.hidden = false;
        attachStatus.textContent = 'Foto anexada';
        photoLabel.textContent = 'Trocar foto';
        syncHasText();
    });

    attach.querySelector('[data-action="photo-remove"]').addEventListener('click', () => {
        clearPhoto();
        card.querySelector('[data-action="photo"]').focus();
    });

    let googleRendered = false;
    function renderGoogleButton() {
        if (googleRendered) return;
        const slot = login.querySelector('[data-google-button]');
        let tries = 0;
        const attempt = () => {
            const gsi = window.google && window.google.accounts && window.google.accounts.id;
            if (gsi) {
                googleRendered = true;
                gsi.renderButton(slot, {
                    type: 'standard', shape: 'pill', theme: 'outline', text: 'signin_with',
                    size: 'large', locale: 'pt-BR', logo_alignment: 'left',
                });
                return;
            }
            tries += 1;
            if (tries < 25) {
                setTimeout(attempt, 200);
                return;
            }
            // GSI bloqueado ou lento: o login do próprio Lume leva ao mesmo destino
            googleRendered = true;
            const a = document.createElement('a');
            a.className = 'lpi-btn lpi-btn--primary';
            a.href = chatUrl;
            a.textContent = 'Entrar com Google';
            slot.appendChild(a);
        };
        attempt();
    }

    const webview = login.querySelector('.lpi-webview');
    const inAppBrowser = isInAppBrowser();
    if (inAppBrowser) {
        const open = webview.querySelector('[data-action="webview-open"]');
        const copy = webview.querySelector('[data-action="webview-copy"]');
        open.href = openInBrowserHref();
        copy.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(window.location.href);
                copy.textContent = 'Link copiado';
            } catch (e) {
                window.prompt('Copie o link e abra no seu navegador:', window.location.href);
            }
        });
    }

    function showLogin() {
        awaitingLogin = true;
        form.hidden = true;
        login.hidden = false;
        push('lume_login_step_view', 'login');
        renderGoogleButton();
        if (inAppBrowser) webview.hidden = false;
        snap();
        const title = login.querySelector('.lpi-login-title');
        if (title) title.focus({ preventScroll: true });
    }

    function showForm() {
        awaitingLogin = false;
        login.hidden = true;
        form.hidden = false;
        snap();
        ta.focus();
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (preparing) return;
        const text = ta.value.trim().slice(0, MAX_QUESTION_CHARS);
        if (!text && !image) {
            showError(MSG_EMPTY);
            ta.focus();
            return;
        }
        // Só a foto bloqueia: sem storage, uma questão de texto segue e o aluno
        // reescreve-a no chat
        const saved = savePendingQuestion({ text, photoDataUrl: image });
        if (!saved && image) {
            showError(MSG_PHOTO);
            return;
        }
        if (saved) push('lume_pending_question_saved', 'question', image ? 'photo' : 'text');

        if (AuthCookies.validate(true)) {
            window.location.href = chatUrl;
            return;
        }
        showLogin();
    });

    form.querySelector('[type="submit"]').disabled = false;

    login.querySelector('[data-action="edit"]').addEventListener('click', () => {
        clearPendingQuestion();
        showForm();
    });

    // O login (Google, popup) acaba aqui sem recarregar. É o evento de sessão
    // que leva ao chat, nunca o fecho de um modal: numa conta nova o formulário
    // de completar perfil está desligado nesta rota (RegisterModalExcludedPaths).
    addUserLoggedEventListener(() => {
        if (awaitingLogin) window.location.href = chatUrl;
    });

    /* ── CTAs que levam ao campo ───────────────────────────────────── */
    root.querySelectorAll('[data-focus-ask]').forEach((link) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            stage.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            setTimeout(() => {
                const target = form.hidden ? login.querySelector('.lpi-login-title') : ta;
                if (target) target.focus({ preventScroll: true });
            }, reduced ? 0 : 650);
        });
    });

    /* ── vídeo (só existe com URL configurado) ─────────────────────── */
    const expand = root.querySelector('[data-action="video"]');
    if (expand) {
        const preview = expand.closest('.lpi-poster').querySelector('video');
        const modal = root.querySelector('.lpi-video-modal');
        const full = modal.querySelector('video');

        // A pré-visualização é muda e em loop, por isso só corre à vista (e não
        // com movimento reduzido); fora do ecrã não gasta dados nem CPU
        const playPreview = () => {
            if (!reduced && !modal.open) preview.play().catch(() => {});
        };
        if (hasIO) {
            new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) playPreview();
                else preview.pause();
            }, { threshold: 0.4 }).observe(preview);
        } else {
            playPreview();
        }

        const onClose = () => {
            full.pause();
            document.documentElement.classList.remove('lpi-modal-open');
            expand.focus({ preventScroll: true });
            playPreview();
        };

        expand.addEventListener('click', () => {
            push('lume_demo_video_play', 'video');
            preview.pause();
            document.documentElement.classList.add('lpi-modal-open');
            if (modal.showModal) modal.showModal();
            else modal.setAttribute('open', '');
            full.currentTime = 0;
            full.play().catch(() => {});
        });

        const close = () => {
            if (modal.close) modal.close();
            else {
                modal.removeAttribute('open');
                onClose();
            }
        };
        modal.addEventListener('close', onClose);
        modal.querySelector('[data-action="video-close"]').addEventListener('click', close);
        // Clique no fundo (fora do vídeo) fecha
        modal.addEventListener('click', (e) => {
            if (e.target === modal) close();
        });
    }

    /* ── reveal, contadores e barra fixa ───────────────────────────── */
    if (hasIO && !reduced) {
        const io = new IntersectionObserver((entries) => entries.forEach((en) => {
            if (!en.isIntersecting) return;
            en.target.classList.remove('is-pre');
            io.unobserve(en.target);
        }), { rootMargin: '0px 0px -8% 0px' });
        root.querySelectorAll('.lpi-reveal').forEach((el) => {
            if (el.getBoundingClientRect().top > window.innerHeight) {
                el.classList.add('is-pre');
                io.observe(el);
            }
        });

        // O servidor já traz o valor final; só os contadores fora do ecrã
        // recomeçam do zero, para não haver salto no que já se vê.
        const fmt = (el, v) => {
            const pre = el.getAttribute('data-prefix') || '';
            const suf = el.getAttribute('data-suffix') || '';
            el.textContent = pre + String(Math.round(v));
            if (suf) {
                const small = document.createElement('small');
                small.textContent = suf;
                el.appendChild(small);
            }
        };
        const no = new IntersectionObserver((entries) => entries.forEach((en) => {
            if (!en.isIntersecting) return;
            no.unobserve(en.target);
            const el = en.target;
            const to = +el.getAttribute('data-count');
            const t0 = performance.now();
            const step = (t) => {
                const p = Math.min(1, (t - t0) / 1400);
                fmt(el, to * (1 - Math.pow(1 - p, 3)));
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }), { threshold: 0.5 });
        root.querySelectorAll('.lpi-num[data-count]').forEach((el) => {
            if (el.getBoundingClientRect().top <= window.innerHeight) return;
            fmt(el, 0);
            no.observe(el);
        });
    }

    // Só aparece quando o cartão do campo sai do ecrã, e sai de cena quando o
    // fecho (que tem o mesmo CTA) entra.
    const sticky = root.querySelector('.lpi-sticky');
    const closing = root.querySelector('.lpi-closing');
    if (sticky && hasIO) {
        let askVisible = true;
        let closingReached = false;
        const update = () => sticky.classList.toggle('is-on', !askVisible && !closingReached);
        new IntersectionObserver((entries) => {
            askVisible = entries[0].isIntersecting;
            update();
        }).observe(card);
        if (closing) {
            new IntersectionObserver((entries) => {
                const en = entries[0];
                closingReached = en.isIntersecting || en.boundingClientRect.top < 0;
                update();
            }).observe(closing);
        }
    }

    /* ── arranque ──────────────────────────────────────────────────── */
    if (window.ResizeObserver) new ResizeObserver(snap).observe(card);
    window.addEventListener('resize', snap);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(snap);
    // Texto reposto pelo browser (reload, voltar sem bfcache)
    if (ta.value.trim()) {
        stopGhost();
        fitTextarea();
    }
    syncHasText();
    snap();
    startGhost();
}());
